import { useCallback, useEffect, useState } from "react";

import { getFingerprint } from "react-fingerprint";
import { useDispatch } from "react-redux";

import { SOCKET_URL } from "@/config/api";
import { QR_CODE_EXPIRE_BY_SECONDS } from "@/constants/app.constants";

import { fetchCookieSession, fetchWebKey } from "../api/auth";
import { AUTH_CONFIG, STORAGE_KEYS } from "../constants/auth.constants";
import { authAction, checkAuthThunk } from "../redux/auth.slice";
import { QRDef } from "../types/auth.types";

interface UseQRLoginReturn {
  qrValue: QRDef | null;
  countDown: number;
  isLoading: boolean;
  error: string | null;
  handleRefreshQR: () => void;
}

const useQRLogin = (): UseQRLoginReturn => {
  const dispatch = useDispatch();
  const [qrValue, setQRValue] = useState<QRDef | null>(null);
  const [countDown, setCountDown] = useState(QR_CODE_EXPIRE_BY_SECONDS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (countDown === 0) {
      setQRValue(null);
      return;
    }

    const interval = setInterval(() => {
      setCountDown((prevCount) => {
        if (prevCount <= 1) {
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countDown]);

  const handleTokenListener = useCallback(
    (webKey: string, fingerprint: string) => {
      let socket: WebSocket | null = null;
      let timeout: NodeJS.Timeout | null = null;

      const cleanup = () => {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.close();
          socket = null;
        }
      };

      try {
        socket = new WebSocket(
          `${SOCKET_URL}/webapp-websocket?webkey=${webKey}&fingerprint=${fingerprint}`
        );

        socket.addEventListener("open", () => {
          console.log("WebSocket connected successfully");
          setError(null);
        });

        socket.addEventListener("message", async (event) => {
          if (event.data) {
            try {
              const cookieSession = await fetchCookieSession(fingerprint);

              if (cookieSession?.data) {
                dispatch(authAction.loginSuccess(true));
                await dispatch(checkAuthThunk() as any);
              } else {
                setError("Không thể xác thực phiên đăng nhập");
              }
            } catch (error) {
              setError("Lỗi xử lý dữ liệu đăng nhập");
              console.error("Error getting cookie session:", error);
            }
          }
          cleanup();
        });

        socket.addEventListener("error", (error) => {
          setError("Lỗi kết nối WebSocket");
          console.error("WebSocket error:", error);
          cleanup();
        });

        socket.addEventListener("close", () => {
          console.log("WebSocket closed");
        });

        timeout = setTimeout(() => {
          console.log("WebSocket timeout reached");
          cleanup();
        }, AUTH_CONFIG.SOCKET_TIMEOUT);

        return cleanup;
      } catch (socketError) {
        setError("Không thể tạo kết nối WebSocket");
        console.error("WebSocket creation error:", socketError);
        cleanup();
        return undefined;
      }
    },
    [dispatch]
  );

  const handleGenerateWebKey = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fingerprint = await getFingerprint();
      const res = await fetchWebKey(fingerprint);

      const cleanup = handleTokenListener(res.webKey, res.fingerprint);

      setQRValue({
        webKey: res.webKey,
        fingerprint: res.fingerprint,
        userAgent: navigator.userAgent,
      });

      localStorage.setItem(STORAGE_KEYS.WEB_KEY, res.webKey);
      localStorage.setItem(STORAGE_KEYS.FINGERPRINT, fingerprint);

      return cleanup;
    } catch (apiError) {
      setError("Không thể tạo mã QR. Vui lòng thử lại.");
      console.error("WebKey generation error:", apiError);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [handleTokenListener]);

  const handleRefreshQR = useCallback(() => {
    setCountDown(QR_CODE_EXPIRE_BY_SECONDS);
    setQRValue(null);
    handleGenerateWebKey();
  }, [handleGenerateWebKey]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const init = async () => {
      cleanup = await handleGenerateWebKey();
    };

    init();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  return {
    qrValue,
    countDown,
    isLoading,
    error,
    handleRefreshQR,
  };
};

export { useQRLogin };
